import React, { useState, useEffect, useCallback } from 'react';
import axiosClient, { STATIC_URL } from '../api/axiosClient';
import {
  PageHeader, Card, Btn, StandardModal, EmptyState, Alert,
  colors, spacing, typography, radius, shadows,
  inputStyle, textareaStyle,
} from '../design-system';

// ── Helpers ────────────────────────────────────────────────────────────────────

const TYPE_LABELS = {
  download:     'Download',
  physical:     'Merchandise',
  merchandise:  'Merchandise',
};

const TYPE_ICONS = {
  download:     '📄',
  physical:     '🛍️',
  merchandise:  '🛍️',
};

const TYPE_COLORS = {
  download:     '#3b82f6',
  physical:     '#f59e0b',
  merchandise:  '#f59e0b',
};

const ORDER_STATUS_LABELS = {
  pending:      'Awaiting Payment',
  payment_sent: 'Payment Sent',
  confirmed:    'Payment Confirmed',
  fulfilled:    'Fulfilled',
  cancelled:    'Cancelled',
};

const ORDER_STATUS_COLORS = {
  pending:      '#f59e0b',
  payment_sent: '#3b82f6',
  confirmed:    '#8b5cf6',
  fulfilled:    '#10b981',
  cancelled:    '#6b7280',
};

function fmt(amount) {
  if (amount === 0) return 'Free';
  return `£${Number(amount).toFixed(2)}`;
}

async function downloadItem(itemId, setDownloading) {
  if (setDownloading) setDownloading(true);
  try {
    const res = await axiosClient.get(`/shop/items/${itemId}/download`, { responseType: 'blob' });
    const disposition = res.headers['content-disposition'] || '';
    const match = disposition.match(/filename[^;=\n]*=(['"]?)([^'";\n]+)\1/);
    const filename = match ? match[2] : `download-${itemId}`;
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (err) {
    let msg = 'Download failed';
    try {
      // When responseType is 'blob', error body is a Blob — read it as text to get the JSON detail
      if (err?.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        const json = JSON.parse(text);
        msg = json.detail || msg;
      } else {
        msg = err?.response?.data?.detail || err?.userMessage || msg;
      }
    } catch {}
    alert(msg);
  } finally {
    if (setDownloading) setDownloading(false);
  }
}

function TypePill({ type }) {
  const color = TYPE_COLORS[type] || '#6b7280';
  return (
    <span style={{
      background: color + '18',
      color,
      border: `1px solid ${color}30`,
      borderRadius: radius.pill,
      fontSize: typography.fontSizes.xs,
      fontWeight: typography.fontWeights.semibold,
      padding: '2px 8px',
    }}>
      {TYPE_ICONS[type]} {TYPE_LABELS[type] || type}
    </span>
  );
}

function OrderStatusBadge({ status }) {
  const color = ORDER_STATUS_COLORS[status] || '#6b7280';
  const label = ORDER_STATUS_LABELS[status] || status;
  return (
    <span style={{
      background: color + '18',
      color,
      border: `1px solid ${color}30`,
      borderRadius: radius.pill,
      fontSize: typography.fontSizes.xs,
      fontWeight: typography.fontWeights.semibold,
      padding: '2px 10px',
    }}>
      {label}
    </span>
  );
}

// ── Item Card ──────────────────────────────────────────────────────────────────

function isImageUrl(url) {
  return url && /\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i.test(url);
}

function resolveStaticUrl(url) {
  if (!url) return null;
  if (url.startsWith('/static/') && STATIC_URL) {
    return `${STATIC_URL}${url.slice('/static'.length)}`;
  }
  return url;
}

function ItemCard({ item, onAddToCart, inCart }) {
  const locked = !item.accessible;
  const [downloading, setDownloading] = useState(false);
  const displayImage = resolveStaticUrl(item.thumbnail_url) || (isImageUrl(item.file_url) ? resolveStaticUrl(item.file_url) : null);

  return (
    <div style={{
      background: colors.surface,
      border: `1px solid ${colors.border}`,
      borderRadius: radius.lg,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      opacity: locked ? 0.7 : 1,
      boxShadow: shadows.cardShadow,
      transition: 'box-shadow 150ms ease',
    }}>
      {/* Thumbnail or placeholder */}
      <div style={{
        height: 140,
        background: displayImage
          ? `url(${displayImage}) center/contain no-repeat, linear-gradient(135deg, ${TYPE_COLORS[item.item_type] || '#6b7280'}20, ${TYPE_COLORS[item.item_type] || '#6b7280'}08)`
          : `linear-gradient(135deg, ${TYPE_COLORS[item.item_type] || '#6b7280'}20, ${TYPE_COLORS[item.item_type] || '#6b7280'}08)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '40px',
        borderBottom: `1px solid ${colors.border}`,
        position: 'relative',
      }}>
        {!displayImage && TYPE_ICONS[item.item_type]}
        {locked && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'rgba(0,0,0,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: typography.fontSizes.sm,
            color: '#fff',
            fontWeight: typography.fontWeights.semibold,
            gap: spacing.xs,
          }}>
            🔒 Accreditation Required
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{ padding: spacing.md, flex: 1, display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
        <TypePill type={item.item_type} />

        <div style={{ fontSize: typography.fontSizes.md, fontWeight: typography.fontWeights.semibold, color: colors.textPrimary, lineHeight: 1.3 }}>
          {item.title}
        </div>

        {item.description && (
          <div style={{ fontSize: typography.fontSizes.sm, color: colors.textMuted, lineHeight: 1.5, flex: 1 }}>
            {item.description.length > 120 ? item.description.slice(0, 120) + '…' : item.description}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto', paddingTop: spacing.sm }}>
          <span style={{
            fontSize: typography.fontSizes.lg,
            fontWeight: typography.fontWeights.bold,
            color: item.is_free ? '#10b981' : colors.textPrimary,
          }}>
            {fmt(item.is_free ? 0 : item.price)}
          </span>

          {locked ? (
            <span style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted }}>Members only</span>
          ) : item.is_free && item.file_url ? (
            <Btn
              variant="success"
              size="sm"
              loading={downloading}
              onClick={() => downloadItem(item.id, setDownloading)}
            >
              Download
            </Btn>
          ) : (
            <Btn
              variant={inCart ? 'secondary' : 'primary'}
              size="sm"
              onClick={() => onAddToCart(item)}
              disabled={inCart}
            >
              {inCart ? 'In Cart' : 'Add to Cart'}
            </Btn>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Cart Sidebar ───────────────────────────────────────────────────────────────

function CartSidebar({ cart, onRemove, onClearCart, onCheckout, bankConfig }) {
  const total = cart.reduce((s, i) => s + (i.is_free ? 0 : i.price) * i.qty, 0);

  if (cart.length === 0) {
    return (
      <div style={{ padding: spacing.lg, textAlign: 'center', color: colors.textMuted }}>
        <div style={{ fontSize: '32px', marginBottom: spacing.sm }}>🛒</div>
        <div style={{ fontSize: typography.fontSizes.sm }}>Your cart is empty</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {cart.map(item => (
          <div key={item.id} style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: spacing.sm,
            padding: `${spacing.sm} 0`,
            borderBottom: `1px solid ${colors.border}`,
          }}>
            <span style={{ fontSize: '20px', flexShrink: 0 }}>{TYPE_ICONS[item.item_type]}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textPrimary, lineHeight: 1.3 }}>{item.title}</div>
              <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, marginTop: 2 }}>{fmt(item.is_free ? 0 : item.price)}</div>
            </div>
            <button
              onClick={() => onRemove(item.id)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textMuted, fontSize: '16px', padding: 0, flexShrink: 0 }}
            >
              ×
            </button>
          </div>
        ))}
      </div>

      <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: spacing.md, marginTop: spacing.md }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: typography.fontWeights.bold, fontSize: typography.fontSizes.md, marginBottom: spacing.md }}>
          <span>Total</span>
          <span>{total === 0 ? 'Free' : `£${total.toFixed(2)}`}</span>
        </div>
        <Btn variant="primary" size="md" style={{ width: '100%' }} onClick={onCheckout}>
          Checkout
        </Btn>
        <Btn variant="ghost" size="sm" style={{ width: '100%', marginTop: spacing.sm }} onClick={onClearCart}>
          Clear Cart
        </Btn>
      </div>
    </div>
  );
}

// ── Checkout Modal ─────────────────────────────────────────────────────────────

function CheckoutModal({ cart, bankConfig, onClose, onOrderPlaced }) {
  const [notes, setNotes]   = useState('');
  const [placing, setPlacing] = useState(false);
  const [error, setError]   = useState(null);
  const [shipping, setShipping] = useState({
    name: '', address1: '', address2: '', city: '', zip: '', phone: '', country_code: 'GB',
  });

  const hasPhysical = cart.some(i => i.item_type === 'physical' || i.item_type === 'merchandise');
  const total = cart.reduce((s, i) => s + (i.is_free ? 0 : i.price) * i.qty, 0);
  const allFree = total === 0;
  const setS = (k, v) => setShipping(p => ({ ...p, [k]: v }));

  const place = async () => {
    if (hasPhysical && (!shipping.name || !shipping.address1 || !shipping.city || !shipping.zip)) {
      setError('Please complete the delivery address for your physical items.');
      return;
    }
    setPlacing(true);
    setError(null);
    try {
      const res = await axiosClient.post('/shop/orders', {
        items: cart.map(i => ({ shop_item_id: i.id, quantity: i.qty })),
        notes,
        ...(hasPhysical && {
          shipping_name:         shipping.name,
          shipping_address1:     shipping.address1,
          shipping_address2:     shipping.address2,
          shipping_city:         shipping.city,
          shipping_zip:          shipping.zip,
          shipping_phone:        shipping.phone,
          shipping_country_code: shipping.country_code,
        }),
      });
      onOrderPlaced(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to place order. Please try again.');
      setPlacing(false);
    }
  };

  return (
    <StandardModal
      title="Complete Your Order"
      onClose={onClose}
      width={520}
      footer={
        <div style={{ display: 'flex', gap: spacing.sm, justifyContent: 'flex-end' }}>
          <Btn variant="secondary" size="md" onClick={onClose}>Cancel</Btn>
          <Btn variant="primary"   size="md" onClick={place} loading={placing}>
            {allFree ? 'Place Order (Free)' : 'Place Order & Pay'}
          </Btn>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
        {error && <Alert type="error" message={error} />}

        {/* Order summary */}
        <div>
          <div style={{ fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold, color: colors.textSecondary, marginBottom: spacing.sm }}>Order Summary</div>
          {cart.map(item => (
            <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', padding: `6px 0`, borderBottom: `1px solid ${colors.border}`, fontSize: typography.fontSizes.sm }}>
              <span>{TYPE_ICONS[item.item_type]} {item.title} × {item.qty}</span>
              <span style={{ fontWeight: typography.fontWeights.medium }}>{fmt(item.is_free ? 0 : item.price * item.qty)}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: spacing.sm, fontWeight: typography.fontWeights.bold, fontSize: typography.fontSizes.md }}>
            <span>Total</span>
            <span>{total === 0 ? 'Free' : `£${total.toFixed(2)}`}</span>
          </div>
        </div>

        {/* Bank transfer instructions */}
        {!allFree && bankConfig && (
          <div style={{
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: radius.md,
            padding: spacing.md,
          }}>
            <div style={{ fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.bold, color: '#1d4ed8', marginBottom: spacing.sm }}>
              🏦 Bank Transfer Details
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '4px 8px', fontSize: typography.fontSizes.sm, color: colors.textPrimary }}>
              <span style={{ color: colors.textMuted }}>Bank:</span>        <strong>{bankConfig.bank_name || '—'}</strong>
              <span style={{ color: colors.textMuted }}>Account Name:</span> <strong>{bankConfig.account_name || '—'}</strong>
              <span style={{ color: colors.textMuted }}>Sort Code:</span>    <strong>{bankConfig.sort_code || '—'}</strong>
              <span style={{ color: colors.textMuted }}>Account No.:</span>  <strong>{bankConfig.account_number || '—'}</strong>
              {bankConfig.iban && (<><span style={{ color: colors.textMuted }}>IBAN:</span> <strong>{bankConfig.iban}</strong></>)}
              {bankConfig.bic_swift && (<><span style={{ color: colors.textMuted }}>BIC/Swift:</span> <strong>{bankConfig.bic_swift}</strong></>)}
              {bankConfig.bank_address && (<><span style={{ color: colors.textMuted }}>Bank Address:</span> <strong>{bankConfig.bank_address}</strong></>)}
            </div>
            {bankConfig.checkout_instructions && (
              <div style={{ marginTop: spacing.sm, fontSize: typography.fontSizes.sm, color: '#1e40af', lineHeight: 1.5 }}>
                {bankConfig.checkout_instructions}
              </div>
            )}
            <div style={{ marginTop: spacing.sm, fontSize: typography.fontSizes.xs, color: '#3b82f6' }}>
              After placing your order you will receive a payment reference — use it when making your transfer.
            </div>
          </div>
        )}

        {/* Shipping address — only for physical items */}
        {hasPhysical && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
            <div style={{ fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold, color: colors.textSecondary }}>
              Delivery Address <span style={{ color: colors.danger }}>*</span>
            </div>
            <input style={inputStyle} placeholder="Full name *" value={shipping.name} onChange={e => setS('name', e.target.value)} />
            <input style={inputStyle} placeholder="Address line 1 *" value={shipping.address1} onChange={e => setS('address1', e.target.value)} />
            <input style={inputStyle} placeholder="Address line 2 (optional)" value={shipping.address2} onChange={e => setS('address2', e.target.value)} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.sm }}>
              <input style={inputStyle} placeholder="Town / City *" value={shipping.city} onChange={e => setS('city', e.target.value)} />
              <input style={inputStyle} placeholder="Postcode *" value={shipping.zip} onChange={e => setS('zip', e.target.value)} />
            </div>
            <input style={inputStyle} placeholder="Phone number (optional)" value={shipping.phone} onChange={e => setS('phone', e.target.value)} />
          </div>
        )}

        {/* Customer notes */}
        <div>
          <label style={{ display: 'block', fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textSecondary, marginBottom: 4 }}>
            Notes <span style={{ color: colors.textMuted, fontWeight: 400 }}>(optional)</span>
          </label>
          <textarea style={{ ...textareaStyle, minHeight: 60 }} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any delivery instructions or questions..." />
        </div>
      </div>
    </StandardModal>
  );
}

// ── Order Confirmation Modal ───────────────────────────────────────────────────

function OrderConfirmModal({ order, bankConfig, onClose }) {
  const [ref, setRef]       = useState(order.payment_reference || '');
  const [sending, setSending] = useState(false);
  const [sent, setSent]     = useState(false);
  const [error, setError]   = useState(null);

  const total = order.total_amount;
  const allFree = total === 0;

  const confirmPayment = async () => {
    if (!ref.trim()) return;
    setSending(true);
    try {
      await axiosClient.post(`/shop/orders/${order.id}/confirm-payment`, { payment_reference: ref });
      setSent(true);
    } catch {
      setError('Could not send confirmation. Please contact support.');
    } finally {
      setSending(false);
    }
  };

  return (
    <StandardModal
      title="Order Placed!"
      onClose={onClose}
      width={500}
      footer={
        <Btn variant="primary" size="md" onClick={onClose}>Done</Btn>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: spacing.sm }}>✅</div>
          <div style={{ fontSize: typography.fontSizes.lg, fontWeight: typography.fontWeights.bold, color: colors.textPrimary }}>
            Order {order.order_number}
          </div>
          <div style={{ fontSize: typography.fontSizes.sm, color: colors.textMuted, marginTop: 4 }}>
            {allFree ? 'Your free order is being processed.' : `Total: £${total.toFixed(2)}`}
          </div>
        </div>

        {!allFree && !sent && (
          <div style={{
            background: '#fefce8',
            border: '1px solid #fde047',
            borderRadius: radius.md,
            padding: spacing.md,
          }}>
            <div style={{ fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.bold, color: '#92400e', marginBottom: spacing.sm }}>
              Next Step: Make Your Payment
            </div>
            {bankConfig && (
              <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '4px 8px', fontSize: typography.fontSizes.sm, marginBottom: spacing.sm }}>
                <span style={{ color: colors.textMuted }}>Bank:</span>         <strong>{bankConfig.bank_name}</strong>
                <span style={{ color: colors.textMuted }}>Account Name:</span> <strong>{bankConfig.account_name}</strong>
                <span style={{ color: colors.textMuted }}>Sort Code:</span>    <strong>{bankConfig.sort_code}</strong>
                <span style={{ color: colors.textMuted }}>Account No.:</span>  <strong>{bankConfig.account_number}</strong>
                <span style={{ color: colors.textMuted }}>Reference:</span>    <strong style={{ color: '#b45309' }}>{order.payment_reference}</strong>
              </div>
            )}
            <div style={{ fontSize: typography.fontSizes.xs, color: '#92400e' }}>
              Use <strong>{order.payment_reference}</strong> as your payment reference.
            </div>
          </div>
        )}

        {!allFree && !sent && (
          <div>
            <div style={{ fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textSecondary, marginBottom: 4 }}>
              Once you've sent the payment, enter your bank transfer reference below:
            </div>
            {error && <Alert type="error" message={error} />}
            <div style={{ display: 'flex', gap: spacing.sm }}>
              <input
                style={{ ...inputStyle, flex: 1 }}
                value={ref}
                onChange={e => setRef(e.target.value)}
                placeholder="Your bank's transaction reference"
              />
              <Btn variant="primary" size="md" onClick={confirmPayment} loading={sending}>
                Confirm Sent
              </Btn>
            </div>
          </div>
        )}

        {sent && (
          <Alert type="success" message="Payment confirmation sent. We'll process your order once we receive the funds." />
        )}
      </div>
    </StandardModal>
  );
}

// ── My Orders ─────────────────────────────────────────────────────────────────

function MyOrders({ orders, loading, bankConfig }) {
  if (loading) return <div style={{ textAlign: 'center', color: colors.textMuted, padding: spacing.xl }}>Loading orders…</div>;
  if (!orders.length) return <EmptyState icon="📦" title="No orders yet" subtitle="Items you purchase will appear here." />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
      {orders.map(order => (
        <Card key={order.id}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: spacing.sm }}>
            <div>
              <div style={{ fontSize: typography.fontSizes.md, fontWeight: typography.fontWeights.semibold, color: colors.textPrimary }}>
                {order.order_number}
              </div>
              <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted }}>
                {new Date(order.created_at).toLocaleDateString()}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
              <span style={{ fontWeight: typography.fontWeights.bold, color: colors.textPrimary }}>
                {order.total_amount === 0 ? 'Free' : `£${order.total_amount.toFixed(2)}`}
              </span>
              <OrderStatusBadge status={order.status} />
            </div>
          </div>

          <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: spacing.sm }}>
            {order.items.map((oi, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: typography.fontSizes.sm, padding: '3px 0' }}>
                <span style={{ color: colors.textSecondary }}>{TYPE_ICONS[oi.item_type]} {oi.title} × {oi.quantity}</span>
                {order.status === 'fulfilled' && oi.item_type === 'download' && (
                  <Btn variant="ghost" size="sm" onClick={() => downloadItem(oi.shop_item_id)}>
                    Download
                  </Btn>
                )}
              </div>
            ))}
          </div>

          {order.status === 'pending' && (
            <div style={{ marginTop: spacing.sm, fontSize: typography.fontSizes.xs, color: '#92400e', background: '#fef3c7', borderRadius: radius.sm, padding: `4px ${spacing.sm}` }}>
              Use reference <strong>{order.payment_reference}</strong> for your bank transfer.
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

// ── Filter Pills ───────────────────────────────────────────────────────────────

function FilterBar({ active, onChange }) {
  const filters = [
    { value: 'all',      label: 'All' },
    { value: 'download', label: '📄 Downloads' },
    { value: 'physical', label: '🛍️ Merchandise' },
  ];
  return (
    <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', marginBottom: spacing.lg }}>
      {filters.map(f => (
        <button
          key={f.value}
          onClick={() => onChange(f.value)}
          style={{
            padding: `6px 14px`,
            borderRadius: radius.pill,
            border: `1px solid ${active === f.value ? colors.primary : colors.border}`,
            background: active === f.value ? colors.primary : colors.surface,
            color: active === f.value ? '#fff' : colors.textSecondary,
            cursor: 'pointer',
            fontSize: typography.fontSizes.sm,
            fontWeight: typography.fontWeights.medium,
            transition: 'all 120ms',
          }}
        >
          {f.label}
        </button>
      ))}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

const TABS = [
  { key: 'shop',   label: 'Store' },
  { key: 'orders', label: 'My Orders' },
];

export default function AccreditationStore() {
  const [activeTab, setActiveTab] = useState('shop');
  const [filter,    setFilter]    = useState('all');

  const [items,      setItems]      = useState([]);
  const [myOrders,   setMyOrders]   = useState([]);
  const [bankConfig, setBankConfig] = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const [cart, setCart] = useState([]);

  const [showCart,    setShowCart]    = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [confirmedOrder, setConfirmedOrder] = useState(null);

  const [msg, setMsg] = useState(null);
  const flash = (text, type = 'success') => { setMsg({ text, type }); setTimeout(() => setMsg(null), 4000); };

  const loadStore = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, cfgRes] = await Promise.all([
        axiosClient.get('/shop/store'),
        axiosClient.get('/shop/store/config'),
      ]);
      setItems(itemsRes.data);
      setBankConfig(cfgRes.data);
    } catch {
      flash('Failed to load store.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const res = await axiosClient.get('/shop/my-orders');
      setMyOrders(res.data);
    } catch {
      flash('Failed to load orders.', 'error');
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  useEffect(() => { loadStore(); }, [loadStore]);

  useEffect(() => {
    if (activeTab === 'orders') loadOrders();
  }, [activeTab, loadOrders]);

  const addToCart = (item) => {
    setCart(prev => {
      if (prev.find(i => i.id === item.id)) return prev;
      return [...prev, { ...item, qty: 1 }];
    });
    flash(`${item.title} added to cart.`);
  };

  const removeFromCart = (id) => setCart(prev => prev.filter(i => i.id !== id));
  const clearCart      = ()   => setCart([]);

  const handleOrderPlaced = (order) => {
    setShowCheckout(false);
    clearCart();
    setConfirmedOrder(order);
  };

  const cartIds = new Set(cart.map(i => i.id));
  const filtered = filter === 'all' ? items : items.filter(i => i.item_type === filter);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: spacing.lg }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: spacing.lg }}>
        <PageHeader
          title="The ICSC Store"
          subtitle="Downloads, resources, and products for accredited organisations"
        />
        <div style={{ position: 'relative', marginTop: 4 }}>
          <Btn variant="secondary" size="md" onClick={() => setShowCart(true)}>
            🛒 Cart {cart.length > 0 && (
              <span style={{
                background: colors.primary,
                color: '#fff',
                borderRadius: '50%',
                width: 18,
                height: 18,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 700,
                marginLeft: 4,
              }}>
                {cart.length}
              </span>
            )}
          </Btn>
        </div>
      </div>

      {msg && <Alert type={msg.type} message={msg.text} />}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: spacing.xs, borderBottom: `2px solid ${colors.border}`, marginBottom: spacing.lg }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              padding: `${spacing.sm} ${spacing.lg}`,
              background: 'none',
              border: 'none',
              borderBottom: activeTab === t.key ? `2px solid ${colors.primary}` : '2px solid transparent',
              marginBottom: -2,
              cursor: 'pointer',
              fontSize: typography.fontSizes.md,
              fontWeight: activeTab === t.key ? typography.fontWeights.semibold : typography.fontWeights.medium,
              color: activeTab === t.key ? colors.primary : colors.textSecondary,
              transition: 'all 120ms',
            }}
          >
            {t.label}
            {t.key === 'orders' && myOrders.length > 0 && (
              <span style={{ marginLeft: 6, background: colors.border, borderRadius: radius.pill, padding: '1px 6px', fontSize: typography.fontSizes.xs }}>
                {myOrders.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Shop tab ── */}
      {activeTab === 'shop' && (
        <>
          <FilterBar active={filter} onChange={setFilter} />

          {loading ? (
            <div style={{ textAlign: 'center', color: colors.textMuted, padding: spacing.xl }}>Loading store…</div>
          ) : filtered.length === 0 ? (
            <EmptyState icon="🏪" title="No items available" subtitle="Check back soon for new resources and products." />
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: spacing.lg,
            }}>
              {filtered.map(item => (
                <ItemCard
                  key={item.id}
                  item={item}
                  onAddToCart={addToCart}
                  inCart={cartIds.has(item.id)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* ── My Orders tab ── */}
      {activeTab === 'orders' && (
        <MyOrders orders={myOrders} loading={ordersLoading} bankConfig={bankConfig} />
      )}

      {/* ── Cart modal ── */}
      {showCart && (
        <StandardModal
          title={`Cart (${cart.length} item${cart.length !== 1 ? 's' : ''})`}
          onClose={() => setShowCart(false)}
          width={400}
          footer={
            cart.length > 0 ? (
              <Btn variant="primary" size="md" onClick={() => { setShowCart(false); setShowCheckout(true); }} style={{ width: '100%' }}>
                Proceed to Checkout
              </Btn>
            ) : null
          }
        >
          <CartSidebar
            cart={cart}
            onRemove={removeFromCart}
            onClearCart={clearCart}
            onCheckout={() => { setShowCart(false); setShowCheckout(true); }}
            bankConfig={bankConfig}
          />
        </StandardModal>
      )}

      {/* ── Checkout modal ── */}
      {showCheckout && (
        <CheckoutModal
          cart={cart}
          bankConfig={bankConfig}
          onClose={() => setShowCheckout(false)}
          onOrderPlaced={handleOrderPlaced}
        />
      )}

      {/* ── Order confirmation modal ── */}
      {confirmedOrder && (
        <OrderConfirmModal
          order={confirmedOrder}
          bankConfig={bankConfig}
          onClose={() => { setConfirmedOrder(null); loadOrders(); }}
        />
      )}
    </div>
  );
}
